import type Owner from '@ember/owner';
import { setOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task, type TaskInstance } from 'ember-concurrency';
import type {
  ParityInsightHistoricalData,
  ParityInsightSegmentationData,
  ParityInsightSegmentationItem,
} from 'lumen/models';
import {
  SEGMENTATION_KEYS,
  SEGMENTATION_VALUE_TYPES,
  type SegmentationItemLike,
  type SegmentationKey,
} from 'lumen/models/parity/insight/segmentation-item';
import type { RouteQueryParams } from 'lumen/routes/market/parity/insight';
import type {
  MealTypeValues,
  MembershipType,
} from 'lumen/utils/constants/general-constants';
import {
  APIGATEWAY,
  MEMBERSHIPTYPES,
} from 'lumen/utils/constants/general-constants';
import type {
  Breakdown,
  CountryCodeWithDefault,
  DateAggregation,
  FromDateType,
  RateType,
} from 'lumen/utils/constants/pi-constant-values';
import {
  HISTORICAL_POINTS_AMOUNT,
  SINGLE_SELECT_BREAKDOWNS,
} from 'lumen/utils/constants/pi-constant-values';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import SilentTaskError from 'lumen/utils/silent-task-error';
import { hash } from 'rsvp';

type ParityAnalyticsRequestParams = (
  | { subscription_id: string }
  | { market_id: string }
) & {
  base_membership_type?: MembershipType;
  date_aggregation: DateAggregation;
  extract_date: string;
  filters?: string;
  from_date_type: FromDateType;
  los: number;
  mealtype: MealTypeValues;
  periods?: number;
  pos: CountryCodeWithDefault;
  rate_type?: RateType;
};

export default class DataFetching {
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];

  @tracked filteredCache: ParityInsightSegmentationData | null = null;
  @tracked filteredHistoricalCache: ParityInsightHistoricalData | null = null;
  @tracked unfilteredCache: ParityInsightSegmentationData | null = null;
  @tracked unfilteredHistoricalCache: ParityInsightHistoricalData | null = null;

  #isSingleProperty: boolean;

  requestFilteredHistoricalDataTask = task(
    { restartable: true },
    async (params: RouteQueryParams) => {
      try {
        const requestParams: ParityAnalyticsRequestParams = {
          ...this.requestParams,
          periods: HISTORICAL_POINTS_AMOUNT,
          extract_date: params.historicalExtractDate,
          date_aggregation: params.dateAggregation,
          rate_type: params.rateType,
          los: params.los,
          pos: params.pos,
          mealtype: params.mealPlan,
          from_date_type: params.fromDateType,
        };
        if (params.segmentationFilters) {
          requestParams['filters'] = params.segmentationFilters;
        }
        if (params.showMemberRate) {
          requestParams['base_membership_type'] = MEMBERSHIPTYPES.MEMBER_ONLY;
        }
        const filteredHistoricalCache =
          (await this.store.queryRecord<ParityInsightHistoricalData>(
            'parity/insight/historical-data',
            {
              ...requestParams,
              namespace: APIGATEWAY,
              typeKey: this.apiPath,
            },
          ))!;
        this.filteredHistoricalCache = filteredHistoricalCache;
        return filteredHistoricalCache;
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getAPIErrorMessage(error, this.intl));
      }
    },
  );

  requestFilteredSegmentationDataTask = task(
    { restartable: true },
    async (params: RouteQueryParams) => {
      try {
        const requestParams: ParityAnalyticsRequestParams = {
          ...this.requestParams,
          extract_date: params.extractDate,
          date_aggregation: params.dateAggregation,
          rate_type: params.rateType,
          los: params.los,
          pos: params.pos,
          mealtype: params.mealPlan,
          from_date_type: params.fromDateType,
        };
        if (params.segmentationFilters) {
          requestParams['filters'] = params.segmentationFilters;
        }
        if (params.showMemberRate) {
          requestParams['base_membership_type'] = MEMBERSHIPTYPES.MEMBER_ONLY;
        }
        return (await this.store.queryRecord<ParityInsightSegmentationData>(
          'parity/insight/segmentation-data',
          {
            ...requestParams,
            namespace: APIGATEWAY,
            typeKey: this.apiPath,
          },
        ))!;
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getAPIErrorMessage(error, this.intl));
      }
    },
  );

  requestSegmentationDataFromCacheTask = task(
    { restartable: true },
    async (params: RouteQueryParams) => {
      try {
        const filteredSegmentationData =
          await this.requestFilteredSegmentationDataTask.perform(params);
        this.mergeResponses(filteredSegmentationData, this.unfilteredCache!);
        this.handleSegmentationParentsAndChildren(filteredSegmentationData);
        this.filteredCache = filteredSegmentationData;
        return filteredSegmentationData;
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getAPIErrorMessage(error, this.intl));
      }
    },
  );

  requestSegmentationDataTask = task(
    { restartable: true },
    async (params: RouteQueryParams) => {
      try {
        const allRequests: {
          filteredSegmentations?: TaskInstance<ParityInsightSegmentationData>;
          unfilteredSegmentations: TaskInstance<ParityInsightSegmentationData>;
        } = {
          unfilteredSegmentations:
            this.requestUnfilteredSegmentationDataTask.perform(params),
        };

        if (this.hasActiveFilters(params)) {
          allRequests.filteredSegmentations =
            this.requestFilteredSegmentationDataTask.perform(params);
        }

        const { filteredSegmentations, unfilteredSegmentations } =
          await hash(allRequests);

        this.unfilteredCache = unfilteredSegmentations;

        if (this.hasActiveFilters(params) && filteredSegmentations) {
          this.mergeResponses(filteredSegmentations, unfilteredSegmentations);
          this.handleSegmentationParentsAndChildren(filteredSegmentations);
          this.filteredCache = filteredSegmentations;
          return filteredSegmentations;
        }

        return unfilteredSegmentations;
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getAPIErrorMessage(error, this.intl));
      }
    },
  );

  requestUnfilteredHistoricalDataTask = task(
    { restartable: true },
    async (params: RouteQueryParams) => {
      try {
        const requestParams: ParityAnalyticsRequestParams = {
          ...this.requestParams,
          periods: HISTORICAL_POINTS_AMOUNT,
          extract_date: params.historicalExtractDate,
          date_aggregation: params.dateAggregation,
          rate_type: params.rateType,
          los: params.los,
          pos: params.pos,
          mealtype: params.mealPlan,
          from_date_type: params.fromDateType,
        };
        if (params.showMemberRate) {
          requestParams['base_membership_type'] = MEMBERSHIPTYPES.MEMBER_ONLY;
        }

        const unfilteredHistoricalData =
          (await this.store.queryRecord<ParityInsightHistoricalData>(
            'parity/insight/historical-data',
            {
              ...requestParams,
              namespace: APIGATEWAY,
              typeKey: this.apiPath,
            },
          ))!;
        this.unfilteredHistoricalCache = unfilteredHistoricalData;
        return unfilteredHistoricalData;
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getAPIErrorMessage(error, this.intl));
      }
    },
  );

  requestUnfilteredSegmentationDataTask = task(
    { restartable: true },
    async (params: RouteQueryParams) => {
      try {
        const requestParams: ParityAnalyticsRequestParams = {
          ...this.requestParams,
          extract_date: params.extractDate,
          date_aggregation: params.dateAggregation,
          rate_type: params.rateType,
          los: params.los,
          pos: params.pos,
          mealtype: params.mealPlan,
          from_date_type: params.fromDateType,
        };
        if (params.showMemberRate) {
          requestParams['base_membership_type'] = MEMBERSHIPTYPES.MEMBER_ONLY;
        }
        const record =
          (await this.store.queryRecord<ParityInsightSegmentationData>(
            'parity/insight/segmentation-data',
            {
              ...requestParams,
              namespace: APIGATEWAY,
              typeKey: this.apiPath,
            },
          ))!;
        this.handleSegmentationParentsAndChildren(record);
        return record;
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getAPIErrorMessage(error, this.intl));
      }
    },
  );

  get apiPath(): string {
    return this.#isSingleProperty
      ? 'parityanalytics/api/single_property'
      : 'parityanalytics/api';
  }

  get requestParams(): { subscription_id: string } | { market_id: string } {
    return this.#isSingleProperty
      ? { subscription_id: this.hotelAndMarketService.hotelOrMarket.id! }
      : { market_id: this.hotelAndMarketService.hotelOrMarket.id! };
  }

  constructor(owner: Owner, params: { isSingleProperty: boolean }) {
    setOwner(this, owner);
    this.#isSingleProperty = params.isSingleProperty;
  }

  clearCachedData(): void {
    this.filteredCache = null;
    this.filteredHistoricalCache = null;
    this.unfilteredCache = null;
    this.unfilteredHistoricalCache = null;
    this.store.unloadAll('parity/insight/segmentation-data');
    this.store.unloadAll('parity/insight/historical-data');
    this.store.unloadAll('parity/insight/segmentation-item');
  }

  handleSegmentationParentsAndChildren(
    record: ParityInsightSegmentationData,
  ): void {
    const objValueById: Partial<
      Record<string, Record<string, ParityInsightSegmentationItem>>
    > = {};

    Object.values(SEGMENTATION_KEYS).forEach((segmentName: SegmentationKey) => {
      const items: ParityInsightSegmentationItem[] = record[segmentName];

      if (!items?.length) {
        return;
      }

      const childItems: ParityInsightSegmentationItem[] = [];
      objValueById[segmentName] = {};

      const allItems = record[SEGMENTATION_KEYS.ALL];

      items.forEach((item) => {
        if (
          allItems?.length &&
          allItems[0]?.value &&
          (segmentName === SEGMENTATION_KEYS.VARIANCE ||
            segmentName === SEGMENTATION_KEYS.ISSUE_TYPE)
        ) {
          const allValue = allItems[0].value;
          item.overridingTotal =
            (allValue.win ?? 0) + (allValue.meet ?? 0) + (allValue.loss ?? 0);
        }

        if (item.segmentationValueType === SEGMENTATION_VALUE_TYPES.PARENT) {
          item.children = [];
          item.isExpanded = false;
        }

        item.isSelectable = true;

        if (
          item.segmentationValueType === SEGMENTATION_VALUE_TYPES.CHILD &&
          item.parentId
        ) {
          childItems.push(item);
        }

        objValueById[segmentName]![item.id!] = item;
      });

      childItems.forEach((child) => {
        const parentId = child.parentId;
        const parentCompoundId = parentId
          ? `${parentId}__${child.extractDate}`
          : null;
        if (parentCompoundId && objValueById[segmentName]?.[parentCompoundId]) {
          const parent = objValueById[segmentName][parentCompoundId];
          if (!parent.children) {
            parent.children = [];
          }
          parent.children.push(child);
          delete objValueById[segmentName][child.id!];
        }
      });
    });
  }

  hasActiveFilters(params: RouteQueryParams): boolean {
    return Boolean(params.segmentationFilters);
  }

  /**
   * Merges two responses into one response. Mainly used to merge filtered/unfiltered or with cache to fill up gaps
   * This merging is especially necessary because filtered responses only contain those values that are different for that request
   * The values that did not change appear as undefined, and are then filled up by this function.
   * It is important to respect the order of arguments. Unfiltered or cache should come as a second argument.
   * ⚠️ When either of the arguments is not defined for some reason, the defined data is passed through
   * @param  {Object} filteredResponse   Segmentation Object from the filtered response
   * @param  {Object} unfilteredResponse Segmentation Object from the unfiltered response or cache
   * @return {Object}                    Segmentation Object as a consolidation of the two (OR)
   */
  mergeResponses(
    filtered: ParityInsightSegmentationData,
    unfiltered: ParityInsightSegmentationData,
  ): void {
    Object.values(SEGMENTATION_KEYS).forEach((key) => {
      const filteredItems: SegmentationItemLike[] = filtered[key];
      const unfilteredItems: ParityInsightSegmentationItem[] = unfiltered[key];

      if (
        !filteredItems ||
        !unfilteredItems ||
        SINGLE_SELECT_BREAKDOWNS.includes(key as Breakdown)
      ) {
        return;
      }

      const filteredByUnfilteredId = new Map<string, SegmentationItemLike>();
      filteredItems.forEach((filteredItem) => {
        if (filteredItem.unfilteredId) {
          filteredByUnfilteredId.set(
            `${filteredItem.unfilteredId}__${filteredItem.extractDate}`,
            filteredItem,
          );
        }
      });

      unfilteredItems.forEach((unfilteredItem) => {
        const match = filteredByUnfilteredId.get(unfilteredItem.id!);

        if (match) {
          match.unfilteredData = unfilteredItem;
        } else {
          // Create dummy record for filterd out data that still needs to be represented in the UI
          filteredItems.push(
            this.store.createRecord<ParityInsightSegmentationItem>(
              'parity/insight/segmentation-item',
              {
                extractDate: unfilteredItem.extractDate,
                segmentationValueId: unfilteredItem.segmentationValueId,
                segmentationValue: unfilteredItem.segmentationValue,
                segmentationId: unfilteredItem.segmentationId,
                segmentationValueType: unfilteredItem.segmentationValueType,
                meta: unfilteredItem.meta,
                value: { win: 0, meet: 0, loss: 0, subscriptions: 0 },
                unfilteredData: unfilteredItem,
              },
            ),
          );
        }
      });
    });
  }

  requestHistoricalDataTask(
    params: RouteQueryParams,
  ): TaskInstance<ParityInsightHistoricalData> {
    if (this.hasActiveFilters(params)) {
      return this.requestFilteredHistoricalDataTask.perform(params);
    }

    return this.requestUnfilteredHistoricalDataTask.perform(params);
  }
}
